import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  getDashboard,
  isGuarded,
  runDashboard,
  type AppliedFilter,
  type BlockResult,
  type Dashboard,
} from '../api/dashboards';
import { DashboardBlock } from '../components/dashboards/DashboardBlock';
import { FilterBar } from '../components/dashboards/FilterBar';
import type { Visibility } from '../api/visibility';
import { VisibilityControl, VisibilityBadge } from '../components/VisibilityControl';
import { useAuth } from '../auth/useAuth';
import { ForkCard } from '../components/dashboards/ForkCard';
import { AppShell } from '../components/layout/AppShell';
import { Alert, Button, ErrorState, LoadingState } from '../components/ui';
import './DashboardPage.css';

type State = 'loading' | 'ready' | 'error';

/** `/dashboards/:id` — the dashboard, with every block run. */
export function DashboardPage() {
  const { dashboardId = '' } = useParams();

  const [state, setState] = useState<State>('loading');
  const { account } = useAuth();
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [results, setResults] = useState<Record<string, BlockResult>>({});
  const [filters, setFilters] = useState<AppliedFilter[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const run = useCallback(async (id: string, applied: AppliedFilter[] = []) => {
    setRunning(true);
    try {
      const answer = await runDashboard(id, applied);
      setResults(Object.fromEntries(answer.blocks.map((block) => [block.id, block])));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    } finally {
      setRunning(false);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const loaded = await getDashboard(dashboardId);
      setDashboard(loaded);
      setState('ready');
      await run(loaded.id);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, [dashboardId, run]);

  useEffect(() => {
    void load();
  }, [load]);

  // KPIs read as a row of figures above the charts they summarise; the filter
  // controls sit above both, because they govern everything below them.
  const { controls, kpis, rest } = useMemo(() => {
    const blocks = dashboard?.blocks ?? [];
    const isFigure = (type: string) => type === 'kpi' || type === 'statistic';
    return {
      controls: blocks.filter((block) => block.type === 'filter'),
      kpis: blocks.filter((block) => isFigure(block.type)),
      rest: blocks.filter((block) => !isFigure(block.type) && block.type !== 'filter'),
    };
  }, [dashboard]);

  const applyFilters = (applied: AppliedFilter[]) => {
    setFilters(applied);
    if (dashboard) void run(dashboard.id, applied);
  };

  // A block the leakage guard withheld is not a failure: the page is doing its
  // job. Counting it as one would tell the reader the dashboard is broken when
  // it is working (Phase 15).
  const failures = Object.values(results).filter(
    (block) => block.error && !isGuarded(block.error),
  ).length;
  const withheld = Object.values(results).filter((block) => isGuarded(block.error)).length;
  // True when this dashboard's data is not this reader's to read: every result
  // came back summarised, or every block the guard would not answer was
  // withheld. Either way, forking onto "the same data" is not available.
  const derived = withheld > 0 || Object.values(results).some((block) => block.result?.is_derived);

  if (state === 'loading') {
    return (
      <AppShell>
        <LoadingState label="Brewing…" />
      </AppShell>
    );
  }

  if (state === 'error' || !dashboard) {
    return (
      <AppShell>
        <ErrorState
          title="We could not open this dashboard"
          description={error?.message}
          requestId={error?.requestId}
          onRetry={() => void load()}
        />
      </AppShell>
    );
  }

  return (
    <AppShell
      header={
        <div className="sc-page-header">
          <div>
            <h1>{dashboard.title}</h1>
            <p>
              <VisibilityBadge value={dashboard.visibility} /> {dashboard.description}
              {dashboard.forked_from && dashboard.forked_from_title && (
                <>
                  {' · '}
                  <span className="sc-dashboard__origin">
                    Forked from “{dashboard.forked_from_title}”
                    {dashboard.forked_from_author && ` by @${dashboard.forked_from_author}`}
                  </span>
                </>
              )}
            </p>
          </div>
          <div className="sc-page-header__actions">
            {dashboard.is_mine && (
              <Link to={`/datasets/${dashboard.dataset}`}>The data behind it</Link>
            )}
            {dashboard.is_mine && (
              <Link to={`/dashboards/${dashboard.id}/edit`}>
                <Button variant="secondary" size="sm">
                  Edit in the Lab
                </Button>
              </Link>
            )}
            <Button
              variant="secondary"
              size="sm"
              isLoading={running}
              onClick={() => void run(dashboard.id, filters)}
            >
              Refresh
            </Button>
          </div>
        </div>
      }
    >
      <FilterBar
        dashboardId={dashboard.id}
        controls={controls}
        value={filters}
        onChange={applyFilters}
      />

      {failures > 0 && (
        <Alert tone="warning" title="Some blocks could not be drawn">
          {failures} of {dashboard.blocks.length} blocks failed. The rest are below, and each one
          says what went wrong.
        </Alert>
      )}

      {withheld > 0 && (
        <Alert tone="info" title="Some figures are not shown here">
          This dashboard is built on data that is not public. Figures that would describe individual
          records are left out; the rest are summarised.
        </Alert>
      )}

      {kpis.length > 0 && (
        <div className="sc-dashboard__kpis">
          {kpis.map((block) => (
            <DashboardBlock key={block.id} block={block} state={results[block.id]} />
          ))}
        </div>
      )}

      <div className="sc-dashboard__grid">
        {rest.map((block) => (
          <div
            key={block.id}
            className="sc-dashboard__cell"
            style={{ gridColumn: `span ${Math.min(block.layout.w, 12)}` }}
          >
            <DashboardBlock block={block} state={results[block.id]} />
          </div>
        ))}
      </div>

      {/* Somebody else's dashboard: the useful thing to offer is not editing
          it, it is asking the same question of your own data. */}
      {!dashboard.is_mine && account && <ForkCard dashboard={dashboard} isDerived={derived} />}

      {dashboard.is_mine && (
        <VisibilityControl
          kind="dashboards"
          id={dashboard.id}
          value={dashboard.visibility}
          noun="dashboard"
          onChanged={(visibility: Visibility) => setDashboard({ ...dashboard, visibility })}
        />
      )}
    </AppShell>
  );
}
